import { useMemo } from 'react';

import { Typography } from '@bookora/ui';

import { encodeQr } from './qr-encode';

export interface QrCodeProps {
  value: string;
  /** Rendered edge length in CSS pixels. */
  size?: number;
  /** Announced to screen readers; the canvas itself carries no information. */
  ariaLabel?: string;
}

/**
 * An SVG QR code, drawn from {@link encodeQr}.
 *
 * ── WHY SVG, AND WHY THE COLOURS ARE LITERAL ────────────────────────────────
 *
 * A scanner needs a light quiet zone and high contrast, and it reads the
 * PHYSICAL image — not the theme. Painting the modules with `--bk-fg` on
 * `--bk-bg` would produce a warm-charcoal-on-near-black code in dark mode that
 * no phone can read. So this is the one place in the panel that names black and
 * white on purpose: they are not brand colours here, they are the encoding.
 *
 * The four-module quiet zone is part of the specification, not padding.
 */
export function QrCode({ value, size = 220, ariaLabel = 'کد QR تأیید دومرحله‌ای' }: QrCodeProps) {
  const matrix = useMemo(() => {
    try {
      return encodeQr(value);
    } catch {
      return null;
    }
  }, [value]);

  if (!matrix) {
    // An encoder throw used to remove the code silently; the secret below the
    // image still enrols, so say that rather than show a hole.
    return (
      <Typography.Text type="secondary" role="status">
        کد QR ساخته نشد؛ کلید متنی زیر رو در برنامهٔ رمز یک‌بارمصرف وارد کنید.
      </Typography.Text>
    );
  }

  const quiet = 4;
  const span = matrix.size + quiet * 2;

  const path: string[] = [];
  for (let row = 0; row < matrix.size; row += 1) {
    for (let col = 0; col < matrix.size; col += 1) {
      if (matrix.modules[row][col]) {
        path.push(`M${col + quiet} ${row + quiet}h1v1h-1z`);
      }
    }
  }

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      width={size}
      height={size}
      viewBox={`0 0 ${span} ${span}`}
      shapeRendering="crispEdges"
      // Never wider than a narrow dialog; the viewBox keeps it square.
      style={{ borderRadius: 'var(--bk-radius-sm)', maxInlineSize: '100%', blockSize: 'auto' }}
    >
      {/* Fixed black-on-white, never a design token: a QR scanner reads module
          contrast, not theme, and a dark-mode tint here would make the code
          unscannable. */}
      <rect width={span} height={span} fill="#FFFFFF" />
      <path d={path.join('')} fill="#000000" />
    </svg>
  );
}
